// Constants and types for cleaning companies.
// This file has NO server dependencies and can be imported in client components.

import type { Region } from "@/generated/prisma/enums";

// Values may be added, renamed or removed freely — but a rename or a removal
// is only half the change. lead-scoring compares these strings to
// CleaningCompany.services[] with an exact match, so the stored rows must move
// in the same commit or the affected companies silently stop matching.
// Glossary terms exist for topics we write about but don't route (hotel-,
// industri-, hospitalsrengøring); a term here means we claim to route it.
export const ALL_SERVICES = [
  "Privat rengøring",
  "Erhvervsrengøring",
  "Vinduespolering",
  "Flytterengøring",
  "Grundrengøring",
  "Dødsbo",
  "Efter håndværker",
  "Tæpperensning",
  "Facaderens",
] as const;

// Display text for a stored service value. Label only: the value in
// ALL_SERVICES and on companies' services[] NEVER changes — hundreds of
// companies have them stored, and lead-matching looks up the exact strings.
//
// "Hovedrengøring" is the word people actually use, and the customer's own
// wizard choice was already internally called `hovedrengoring`, so this
// override fixes an inconsistency rather than creating one. Use this
// everywhere a service is shown to a human.
const SERVICE_LABEL_OVERRIDES: Record<string, string> = {
  Grundrengøring: "Hovedrengøring",
};

export function serviceLabel(service: string): string {
  return SERVICE_LABEL_OVERRIDES[service] ?? service;
}

// The broad service category a client type requires. This is the hard service
// gate in lead-matching (requiredServiceCategory in leads/shared/scoring) AND
// the "who covers privat/erhverv here?" filter in admin — one map so the filter
// can't answer a different question than the matcher.
export const SERVICE_CATEGORY_BY_CLIENT_TYPE: Record<"privat" | "erhverv", string> = {
  privat: "Privat rengøring",
  erhverv: "Erhvervsrengøring",
};

// CleaningCompany.employeeCount stores an EXACT headcount as a string (CVR
// gives a clean integer; the partner enters one directly since CVR data is
// unreliable). These are only <datalist> convenience suggestions — the partner
// can type any number. Some legacy rows hold a range string ("20-49"); the
// public profile renders whatever is stored verbatim, so those still read fine.
export const EMPLOYEE_COUNT_SUGGESTIONS = [1, 2, 3, 5, 10, 20, 50, 100, 250] as const;

export const ALL_REGIONS = [
  "Hovedstaden",
  "Sjælland",
  "Syddanmark",
  "Midtjylland",
  "Nordjylland",
] as const;

// Region slug mapping — keep URL-safe versions of region names.
// Must stay in sync with ALL_REGIONS above.
const REGION_NAME_TO_SLUG: Record<string, string> = {
  Hovedstaden: "hovedstaden",
  Sjælland: "sjaelland",
  Syddanmark: "syddanmark",
  Midtjylland: "midtjylland",
  Nordjylland: "nordjylland",
};

const REGION_SLUG_TO_NAME: Record<string, Region> = {};
for (const name of ALL_REGIONS) {
  const slug = REGION_NAME_TO_SLUG[name];
  if (slug) REGION_SLUG_TO_NAME[slug] = name;
}

export function regionNameToSlug(name: string): string | null {
  return REGION_NAME_TO_SLUG[name] ?? null;
}

export function regionSlugToName(slug: string): Region | null {
  return REGION_SLUG_TO_NAME[slug] ?? null;
}

export function getAllRegionSlugs(): string[] {
  return ALL_REGIONS.map((r) => REGION_NAME_TO_SLUG[r]).filter((s): s is string => Boolean(s));
}

// Formal, verifiable labels a company can hold. Matched by exact string in
// lead-scoring, so the same rename-means-migrate rule as ALL_SERVICES applies.
//
// "Grønt Diplom" was removed 10-09-2026: no certification body issues a scheme
// by that name for cleaning, and from 27-09-2026 (ECGT, marketing act annex 1
// no. 2a) displaying a sustainability mark that rests on no third-party
// certification scheme is always misleading.
export const ALL_CERTIFICATIONS = [
  "Svanemærket",
  "ISO 14001",
  "ISO 9001",
  "Overenskomst (3F)",
  "Arbejdsmiljøcertificeret",
  "EU-Ecolabel",
  "Servicenormen (SBA)",
] as const;

// Self-declared selling points — NOT certifications. Stored on
// CleaningCompany.highlights[]. These are the company's own claims (no
// third-party audit), so the customer-facing UI must render them distinctly
// from ALL_CERTIFICATIONS. Same rename-means-migrate rule as the two above.
export const ALL_HIGHLIGHTS = [
  "Erhvervsansvarsforsikring",
  "Straffeattest på personale",
  "Tilfredshedsgaranti",
  "Fast team/kontaktperson",
  "Dansktalende personale",
  "Gratis besigtigelse",
  "Allergivenlige produkter",
  "Ingen binding",
  "Sikker nøglehåndtering",
  "Akut/weekend-rengøring",
  "Samme person hver gang",
  "Erfaring med husdyr",
  // Product use, not a licence: the Nordic Swan service licence lives in
  // ALL_CERTIFICATIONS. Most companies only use labelled products.
  "Miljømærkede produkter",
] as const;

// Erhverv business types (venue types) a lead can have. For erhverv,
// Lead.cleaningType IS the venue type, set by QuoteWizard.business — these
// values MUST match BUSINESS_TYPES in wizard/quote-config exactly
// (enforced by a unit test), or nothing matches.
//
// Used in two places in matching: positively via
// CleaningCompany.erhvervBusinessTypes (opt-in, empty = all), and negatively
// via the older excludedServices list, which now only covers `andet` and
// non-venue-type values.
export const ALL_BUSINESS_TYPES = [
  { value: "kontor", label: "Kontor" },
  { value: "butik", label: "Butik / restaurant" },
  { value: "klinik", label: "Klinik / sundhed" },
  { value: "industri", label: "Industri / lager" },
  { value: "institution", label: "Institution" },
  { value: "boligforening", label: "Boligforening" },
  { value: "andet", label: "Andet" },
] as const;

// The venue types a partner can pick. `andet` is deliberately excluded: it's
// the customer's "I don't fit the boxes", not a type to specialize in — if it
// were on the list, an Andet lead could only match companies that explicitly
// checked it. Matching therefore skips the filter for `andet` (see
// requiredBusinessType in lead-scoring).
export const PARTNER_BUSINESS_TYPES = ALL_BUSINESS_TYPES.filter((b) => b.value !== "andet");

// One-off erhverv task types a partner can pick. Mirrors BusinessOneTimeType
// from the wizard minus `andet` (same reasoning as above).
export const ERHVERV_TASK_TYPES = [
  { value: "hovedrengoring", label: "Hovedrengøring" },
  { value: "efter-haandvaerker", label: "Efter håndværker" },
  { value: "fraflytning", label: "Fraflytning" },
] as const;

// Labels for the two positive erhverv lists — used in fail reasons and UI.
export function businessTypeLabel(value: string): string {
  return ALL_BUSINESS_TYPES.find((b) => b.value === value)?.label ?? value;
}

export function businessTaskTypeLabel(value: string): string {
  return ERHVERV_TASK_TYPES.find((t) => t.value === value)?.label ?? value;
}

export type CompanyListItem = {
  slug: string;
  name: string;
  city: string | null;
  region: string | null;
  shortDescription: string | null;
  services: string[];
  certifications: string[];
  googleRating: number | null;
  googleReviews: number | null;
  trustpilotScore: number | null;
  trustpilotReviews: number | null;
  logoUrl: string | null;
};

// Slim variant without shortDescription. Used on the city page where the card
// doesn't show the description — saves ~100-300 bytes per company in payload.
export type CityCompanyListItem = Omit<CompanyListItem, "shortDescription">;

// Minimal anchor shape for SSR internal linking. Listed in the city page HTML
// so crawlers find company profiles without serializing the full card.
export type CompanyAnchor = { slug: string; name: string };
